import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, formatApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
    Activity, DollarSign, Loader2, RefreshCw, Trash2, Zap, Timer, Coins, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import BackButton from '../../components/BackButton';

// Format helpers — plain, no locale surprises.
const fmtCost = (n) => `$${(Number(n) || 0).toFixed(6)}`;
const fmtNum = (n) => (Number(n) || 0).toLocaleString('fr-FR');
const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
    } catch {
        return iso;
    }
};

const PURPOSE_LABEL = {
    cv_parse: 'Extraction CV',
    scoring_v1: 'Scoring v1',
    scoring_v2: 'Scoring v2',
    unknown: 'Autre',
};

export default function AdminLlmJournalPage() {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [purpose, setPurpose] = useState('all');
    const [days, setDays] = useState(30);

    useEffect(() => {
        loadAll(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch when filter/pagination changes.
    useEffect(() => {
        if (loading) return;
        loadAll(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, purpose, days]);

    const loadAll = async (initial) => {
        if (!initial) setRefreshing(true);
        try {
            const params = { days };
            if (purpose !== 'all') params.purpose = purpose;
            const [s, l] = await Promise.all([
                adminApi.getLlmJournalStats(params),
                adminApi.getLlmJournal({ ...params, page, page_size: pageSize }),
            ]);
            setStats(s.data);
            setRows(l.data.items || []);
            setTotal(l.data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRefreshing(false);
            if (initial) setLoading(false);
        }
    };

    const handleClear = async () => {
        setClearing(true);
        try {
            const res = await adminApi.clearLlmJournal();
            toast.success(`Journal purgé (${res.data.deleted_count} entrées)`);
            setPage(1);
            await loadAll(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setClearing(false);
        }
    };

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
            </div>
        );
    }

    const kpis = [
        {
            id: 'calls',
            label: 'Appels LLM',
            value: fmtNum(stats?.total_calls ?? 0),
            icon: Activity,
            accent: 'bg-[#DBEAFE] text-[#002FA7]',
        },
        {
            id: 'cost',
            label: 'Coût total (USD)',
            value: fmtCost(stats?.total_cost_usd ?? 0),
            icon: DollarSign,
            accent: 'bg-[#D1FAE5] text-[#059669]',
        },
        {
            id: 'avg_cost',
            label: 'Coût moyen / appel',
            value: fmtCost(stats?.avg_cost_usd ?? 0),
            icon: Coins,
            accent: 'bg-[#EDE9FE] text-[#7C3AED]',
        },
        {
            id: 'tokens',
            label: 'Tokens (in + out)',
            value: fmtNum(stats?.total_tokens ?? 0),
            icon: Zap,
            accent: 'bg-[#FEF3C7] text-[#92400E]',
        },
        {
            id: 'latency',
            label: 'Latence moyenne',
            value: `${fmtNum(stats?.avg_duration_ms ?? 0)} ms`,
            icon: Timer,
            accent: 'bg-[#FCE7F3] text-[#BE185D]',
        },
        {
            id: 'errors',
            label: 'Erreurs',
            value: fmtNum(stats?.errors ?? 0),
            icon: AlertTriangle,
            accent: (stats?.errors ?? 0) > 0 ? 'bg-[#FEE2E2] text-[#DC2626]' : 'bg-[#F3F4F6] text-[#4B5563]',
        },
    ];

    return (
        <div className="space-y-6" data-testid="llm-journal-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <BackButton to="/dashboard/admin" />
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] mt-3 flex items-center gap-2">
                        <DollarSign className="w-6 h-6 text-[#059669]" />
                        Journal de Rentabilité (LLM)
                    </h1>
                    <p className="text-sm text-[#4B5563] mt-1">
                        Coût exact et tokens consommés par chaque appel LLM. Consultable uniquement par un administrateur.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => loadAll(false)}
                        disabled={refreshing}
                        data-testid="llm-journal-refresh-btn"
                    >
                        {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                        {t('common.refresh')}
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button
                                type="button"
                                size="sm"
                                disabled={clearing || (stats?.total_calls ?? 0) === 0}
                                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                                data-testid="llm-journal-clear-btn"
                            >
                                {clearing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                                Purger le journal
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Purger le journal LLM ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Toutes les entrées du journal seront supprimées définitivement ({stats?.total_calls ?? 0} appels).
                                    Les prochains appels réapparaîtront normalement.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={handleClear}
                                    className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                                    data-testid="llm-journal-confirm-clear"
                                >
                                    Purger
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-lg border border-[#E5E7EB]">
                <div className="flex items-center gap-2">
                    <span className="text-xs text-[#4B5563] font-medium">Période</span>
                    <Select value={String(days)} onValueChange={(v) => { setPage(1); setDays(Number(v)); }}>
                        <SelectTrigger className="h-9 w-32" data-testid="llm-journal-days">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">24 h</SelectItem>
                            <SelectItem value="7">7 jours</SelectItem>
                            <SelectItem value="30">30 jours</SelectItem>
                            <SelectItem value="90">90 jours</SelectItem>
                            <SelectItem value="365">1 an</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-[#4B5563] font-medium">Type</span>
                    <Select value={purpose} onValueChange={(v) => { setPage(1); setPurpose(v); }}>
                        <SelectTrigger className="h-9 w-40" data-testid="llm-journal-purpose">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous</SelectItem>
                            <SelectItem value="cv_parse">Extraction CV</SelectItem>
                            <SelectItem value="scoring_v1">Scoring v1</SelectItem>
                            <SelectItem value="scoring_v2">Scoring v2</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4" data-testid="llm-journal-kpis">
                {kpis.map((k) => {
                    const Icon = k.icon;
                    return (
                        <div
                            key={k.id}
                            data-testid={`kpi-${k.id}`}
                            className="bg-white p-4 rounded-lg border border-[#E5E7EB]"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`w-8 h-8 rounded-md flex items-center justify-center ${k.accent}`}>
                                    <Icon className="w-4 h-4" />
                                </span>
                                <span className="text-xs font-medium text-[#4B5563]">{k.label}</span>
                            </div>
                            <p className="text-xl font-bold text-[#0A0A0A] font-['Outfit']">{k.value}</p>
                        </div>
                    );
                })}
            </div>

            {/* Breakdown cards: by purpose & by model */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-lg border border-[#E5E7EB]" data-testid="llm-journal-by-purpose">
                    <h2 className="text-base font-semibold text-[#0A0A0A] mb-3">Répartition par type d'appel</h2>
                    {(!stats?.by_purpose || stats.by_purpose.length === 0) ? (
                        <p className="text-sm text-[#9CA3AF]">Aucun appel enregistré sur la période.</p>
                    ) : (
                        <ul className="space-y-2 text-sm">
                            {stats.by_purpose.map((r) => (
                                <li key={r.purpose} className="flex justify-between items-center py-1.5 border-b border-[#F3F4F6] last:border-0">
                                    <span className="font-medium text-[#0A0A0A]">{PURPOSE_LABEL[r.purpose] || r.purpose}</span>
                                    <div className="flex items-center gap-4 text-xs">
                                        <span className="text-[#4B5563]">{fmtNum(r.calls)} appels</span>
                                        <span className="text-[#4B5563]">{fmtNum(r.prompt_tokens)} in / {fmtNum(r.completion_tokens)} out</span>
                                        <span className="font-semibold text-[#059669]">{fmtCost(r.cost_usd)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="bg-white p-5 rounded-lg border border-[#E5E7EB]" data-testid="llm-journal-by-model">
                    <h2 className="text-base font-semibold text-[#0A0A0A] mb-3">Répartition par modèle</h2>
                    {(!stats?.by_model || stats.by_model.length === 0) ? (
                        <p className="text-sm text-[#9CA3AF]">Aucun appel enregistré sur la période.</p>
                    ) : (
                        <ul className="space-y-2 text-sm">
                            {stats.by_model.map((r) => (
                                <li key={r.model} className="flex justify-between items-center py-1.5 border-b border-[#F3F4F6] last:border-0">
                                    <span className="font-medium text-[#0A0A0A]">{r.model}</span>
                                    <div className="flex items-center gap-4 text-xs">
                                        <span className="text-[#4B5563]">{fmtNum(r.calls)} appels</span>
                                        <span className="text-[#4B5563]">{fmtNum(r.prompt_tokens)} in / {fmtNum(r.completion_tokens)} out</span>
                                        <span className="font-semibold text-[#059669]">{fmtCost(r.cost_usd)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Detailed table */}
            <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden" data-testid="llm-journal-table-wrapper">
                <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                    <h2 className="text-base font-semibold text-[#0A0A0A]">Détail des appels ({fmtNum(total)})</h2>
                    <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                        <span>Page {page} / {totalPages}</span>
                        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="llm-journal-prev">
                            Préc.
                        </Button>
                        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="llm-journal-next">
                            Suiv.
                        </Button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F9FAFB] text-xs uppercase text-[#4B5563]">
                            <tr>
                                <th className="text-left py-2 px-3">Timestamp</th>
                                <th className="text-left py-2 px-3">Type</th>
                                <th className="text-left py-2 px-3">Modèle</th>
                                <th className="text-right py-2 px-3">Tokens IN</th>
                                <th className="text-right py-2 px-3">Tokens OUT</th>
                                <th className="text-right py-2 px-3">Coût (USD)</th>
                                <th className="text-right py-2 px-3">Durée</th>
                                <th className="text-left py-2 px-3">Cible</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-8 text-[#9CA3AF]">Aucun appel sur cette période.</td>
                                </tr>
                            ) : (
                                rows.map((r) => (
                                    <tr key={r.id} className="border-t border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`llm-journal-row-${r.id}`}>
                                        <td className="py-2 px-3 text-[#4B5563] whitespace-nowrap">{fmtDate(r.ts)}</td>
                                        <td className="py-2 px-3">
                                            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-[#EEF2FF] text-[#3730A3]">
                                                {PURPOSE_LABEL[r.purpose] || r.purpose}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 font-mono text-xs text-[#4B5563]">{r.model || '—'}</td>
                                        <td className="py-2 px-3 text-right text-[#4B5563]">{fmtNum(r.prompt_tokens)}</td>
                                        <td className="py-2 px-3 text-right text-[#4B5563]">{fmtNum(r.completion_tokens)}</td>
                                        <td className="py-2 px-3 text-right font-semibold text-[#059669]">{fmtCost(r.cost_usd)}</td>
                                        <td className="py-2 px-3 text-right text-[#4B5563]">{fmtNum(r.duration_ms)} ms</td>
                                        <td className="py-2 px-3 text-xs text-[#4B5563]">
                                            {r.candidate_name && <div>Cand. {r.candidate_name}</div>}
                                            {r.organization_name && <div className="text-[#9CA3AF]">Org. {r.organization_name}</div>}
                                            {!r.candidate_name && !r.organization_name && '—'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
